import { modifier } from 'ember-modifier';

export const enum Orientation {
	VERTICAL = 'vertical',
	HORIZONTAL = 'horizontal',
}

export const enum ResizeMode {
	NORMAL = 'normal',
	INVERSE = 'inverse',
}

const nodeResize = (
	element: HTMLElement,
	[
		orientation = Orientation.VERTICAL,
		onResize = null,
		maxOffsetFromParent = 1,
		parentClassSelector = null,
		handleClass = 'resize-handle',
		mode = ResizeMode.NORMAL,
	]: [Orientation?, ((() => void) | null)?, number?, (string | null)?, string?, ResizeMode?],
) => {
	// Vertical/Horizontal resize
	// maxOffsetFromParent - percentual max height from window (default 1 = 100%)
	// parentClassSelector - if parent class selector is not defined we take window as a parent

	let initialHeight: number, initialWidth: number, initialYPos: number, initialXPos: number;

	const mouseMoveFn = (mouseMoveEvent: MouseEvent) => {
		let parentNodeWidth = window.innerWidth,
			parentNodeHeight = window.innerHeight;

		if (parentClassSelector) {
			const node = element.closest<HTMLElement>(parentClassSelector);
			if (node) {
				parentNodeWidth = node.clientWidth;
				parentNodeHeight = node.clientHeight;
			}
		}

		if (orientation === Orientation.VERTICAL) {
			const posY = mouseMoveEvent.pageY,
				// pos is moving up (bigger), neg is moving down (smaller) (in normal mode)
				changeY = mode === ResizeMode.NORMAL ? initialYPos - posY : posY - initialYPos,
				maxH = parentNodeHeight * maxOffsetFromParent;

			const height = Math.min(maxH, Math.max(0, initialHeight + changeY));
			element.style.height = `${height}px`;
		} else {
			const posX = mouseMoveEvent.pageX,
				changeX = initialXPos - posX,
				maxW = parentNodeWidth * maxOffsetFromParent;

			const width = Math.min(maxW, Math.max(0, initialWidth - changeX));
			element.style.width = `${width}px`;
		}

		if (typeof onResize === 'function') {
			onResize();
		}
	};

	const mouseDownFn = (mouseDownEvent: MouseEvent) => {
		initialHeight = element.offsetHeight;
		initialYPos = mouseDownEvent.pageY;
		initialWidth = element.offsetWidth;
		initialXPos = mouseDownEvent.pageX;

		document.body.addEventListener('mousemove', mouseMoveFn);
		['mouseup', 'mouseleave'].forEach((eventName) => {
			document.body.addEventListener(eventName, mouseUpAndMouseLeaveFn);
		});
	};

	const mouseUpAndMouseLeaveFn = () => {
		document.body.removeEventListener('mousemove', mouseMoveFn);
		['mouseup', 'mouseleave'].forEach((eventName) => {
			document.body.removeEventListener(eventName, mouseUpAndMouseLeaveFn);
		});
	};

	const handle = element.getElementsByClassName(handleClass)[0] as HTMLElement;
	if (handle) {
		handle.addEventListener('mousedown', mouseDownFn);
	}

	return () => {
		if (handle) {
			handle.removeEventListener('mousedown', mouseDownFn);
		}
	};
};

const nodeResizeModifier = modifier(nodeResize);

export default nodeResizeModifier;
